import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import { Box } from "../../atoms/layouts";
import styled from "styled-components";
import { useTurnstileScript } from "../../../hooks";
import { noop } from "../../../util/general";

const TurnstileWidgetContainer = styled(Box)`
  display: flex;
  padding: ${({ padding }) => padding};
  justify-content: ${({ justify }) => justify};
  width: 100%;
`;

const TurnstileWidget = forwardRef(
  (
    {
      verifyURL, // URL to load the Turnstile script from
      siteKey, // Turnstile site key
      onSuccess = noop,
      onError = noop,
      onExpired = noop,
      padding = "1rem",
      justify = "flex-end",
      size = "normal",
      tabindex = 0,
      retry = "auto",
      theme = "auto",
      extraStyles = ""
    },
    ref
  ) => {
    if (!verifyURL || !siteKey) return null;

    const widgetRef = useRef(null);
    const [widgetId, setWidgetId] = useState(null);
    const isTurnstileLoaded = useTurnstileScript(verifyURL);

    // Allow the parent component to reset the Turnstile widget
    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (widgetId && window.turnstile) {
            window.turnstile.reset(widgetId);
          }
        }
      }),
      [widgetId]
    );

    useEffect(() => {
      if (!widgetRef.current || !window.turnstile) return;
      if (!widgetId) {
        const newWidgetId = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          size: size,
          retry: retry,
          tabindex: tabindex,
          theme: theme,
          callback: token => onSuccess(token),
          "error-callback": () => onError(),
          "expired-callback": () => onExpired()
        });
        setWidgetId(newWidgetId);
      }
      return () => {
        if (widgetId && window.turnstile) {
          window.turnstile.remove(widgetId);
          setWidgetId(null);
        }
      };
    }, [isTurnstileLoaded, siteKey, widgetId]);

    return (
      <TurnstileWidgetContainer
        padding={padding}
        justify={justify}
        extraStyles={extraStyles}
      >
        <div ref={widgetRef} />
      </TurnstileWidgetContainer>
    );
  }
);

export default TurnstileWidget;
